/**
 * O único ponto por onde o painel fala com o servidor.
 *
 * ═══ POR QUE UM ARQUIVO SÓ ═══
 *
 * Porque o endereço do backend deixa de ser "o mesmo site". O painel é
 * publicado como estático (Cloudflare Pages) e a API roda em outra
 * máquina (EC2); o motor que lê as câmeras roda numa terceira, na casa
 * do cliente. Três lugares, e a única coisa que o navegador precisa
 * saber é o endereço da API — que vem de VITE_API.
 *
 * Com as chamadas espalhadas, mudar de domínio seria caçar `fetch` pelo
 * projeto inteiro. Aqui é uma variável.
 */

const BASE = (import.meta.env.VITE_API ?? '').replace(/\/+$/, '')

/** Monta o endereço completo de uma rota da API. */
export const url = (rota: string): string =>
  BASE + (rota.startsWith('/') ? rota : `/${rota}`)

/**
 * Erro que veio do servidor com uma mensagem legível.
 *
 * O backend responde `{"detail": "..."}` em português — essas frases são
 * escritas para o usuário ler ("um espaço não pode ser o mesmo lugar que
 * ele mesmo"). Jogá-las fora e mostrar "Erro 400" seria trocar uma
 * explicação por um número.
 */
export class ErroDaApi extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly rota: string,
  ) {
    super(message)
    this.name = 'ErroDaApi'
  }
}

type Opcoes = Omit<RequestInit, 'body'> & { corpo?: unknown; prazoMs?: number }

/* Uma recarga só. Com várias chamadas em voo — e a tela de eventos faz
   quatro de uma vez — todas voltariam 401 juntas e disputariam o
   `replace`, que em alguns navegadores vira histórico sujo. */
let _saindo = false

/* ═══ TODO PEDIDO TEM PRAZO ═══

   `fetch` não tem timeout: sem `AbortController`, um pedido que o
   servidor aceita e não responde fica pendurado até o navegador desistir
   sozinho — o que pode levar minutos, e em alguns casos não acontece.

   Isso importa porque o navegador permite ~6 conexões simultâneas por
   origem. Pedidos pendurados ocupam essas vagas, e a partir daí TODAS as
   telas param de carregar: o clique no menu não traz conteúdo nenhum e
   nada aparece no servidor, porque o pedido nem chegou a sair.

   Foi o que aconteceu em 22/08/2026, com outra causa (a rota de vídeo
   prendia thread no servidor). A causa foi corrigida lá; este prazo é a
   outra metade — o cliente também não pode esperar para sempre.

   30 segundos é folgado para tudo que este painel pede. A exceção é a
   varredura do DVR, que testa canal por canal e leva mais: ela passa o
   próprio prazo. */
const PRAZO_PADRAO_MS = 30_000

async function pedir<T>(rota: string, opcoes: Opcoes = {}): Promise<T> {
  const { corpo, headers, prazoMs, ...resto } = opcoes
  const relogio = new AbortController()
  const alarme = setTimeout(() => relogio.abort(),
                            prazoMs ?? PRAZO_PADRAO_MS)
  let resposta: Response
  try {
    resposta = await fetch(url(rota), {
      ...resto,
      headers: {
        ...(corpo === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
      /* A sessão é um biscoito HttpOnly, e sem isto ele não atravessa a
         fronteira entre o Pages e a EC2 — que são origens diferentes. */
      credentials: 'include',
      signal: relogio.signal,
    })
  } catch (e) {
    /* Mensagem própria para o prazo estourado: "Failed to fetch" faria
       parecer queda de rede, e o caminho para investigar é outro. */
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new Error('O servidor demorou demais para responder.')
    }
    throw e
  } finally {
    clearTimeout(alarme)
  }

  /* ═══ 401 EM QUALQUER ROTA LEVA À ENTRADA ═══

     A sessão dura uma semana sem uso, então ela VAI vencer com a tela
     aberta — o monitor da portaria fica ligado no fim de semana. Sem
     isto, a tela continuaria ali mostrando os dados de sexta-feira e
     cada ação daria um erro diferente, nenhum deles dizendo "entre de
     novo".

     Recarrega em vez de trocar o estado: as telas guardam listas e
     imagens de quem estava logado, e elas têm de sair da memória junto
     com a sessão. Não há laço — `/api/eu` responde 200 mesmo sem
     ninguém logado, e é ela que decide mostrar a tela de entrada. */
  /* ⚠️ MENOS NAS ROTAS DE ENTRAR — e a exceção não é detalhe.
     
     Senha errada responde 401. Sem esta linha, a regra acima entendia
     isso como "sessão expirada" e RECARREGAVA a página — jogando fora,
     em menos de um segundo, justamente a mensagem que explicava o que
     tinha acontecido.
     
     O sintoma era o pior tipo: um piscar. Quem errou a senha via algo
     aparecer e sumir, e ficava sem saber se digitou errado, se o
     sistema caiu, ou se nem chegou a mandar.
     
     A diferença é de significado: 401 numa rota qualquer é "você não
     está mais logado"; 401 em `/api/entrar` é "essas credenciais não
     servem" — e ninguém precisa ser levado à entrada quando já está
     nela. */
  const eEntrada = rota === '/api/entrar' || rota === '/api/primeiro-acesso'

  if (resposta.status === 401 && !_saindo && !eEntrada) {
    _saindo = true
    window.location.replace('/')
    throw new ErroDaApi('Sessão expirada', 401, rota)
  }

  if (!resposta.ok) {
    let mensagem = `${resposta.status} ${resposta.statusText}`
    try {
      const corpoErro = (await resposta.json()) as { detail?: unknown }
      if (typeof corpoErro.detail === 'string') mensagem = corpoErro.detail
    } catch {
      /* resposta sem JSON: fica a mensagem do status */
    }
    throw new ErroDaApi(mensagem, resposta.status, rota)
  }

  if (resposta.status === 204) return undefined as T
  return (await resposta.json()) as T
}

export const api = {
  /* `prazoMs` para o que legitimamente demora — a varredura do DVR testa
     canal por canal e passa dos 30 s padrão quando há canal morto, que
     só responde no fim do tempo do próprio ffmpeg. */
  ler: <T>(rota: string, prazoMs?: number) => pedir<T>(rota, { prazoMs }),
  criar: <T>(rota: string, corpo: unknown) =>
    pedir<T>(rota, { method: 'POST', corpo }),
  trocar: <T>(rota: string, corpo: unknown) =>
    pedir<T>(rota, { method: 'PUT', corpo }),
  ajustar: <T>(rota: string, corpo: unknown) =>
    pedir<T>(rota, { method: 'PATCH', corpo }),
  apagar: <T>(rota: string) => pedir<T>(rota, { method: 'DELETE' }),
}
